import { usePositionActions, type PositionActions } from "../api/usePositionActions";
import {
  BALANCE_NOT_READ,
  calculateStops,
  rangeOrientations,
  stopDistances,
  stopTriggered,
  type ExitPolicy,
  type ExitPolicyRequest,
  type UpdateExitPolicyRequest,
} from "@auto-pools/core";
import { api } from "../api/client";
import { useResource } from "../api/useResource";
import { AUTOPILOT_MANUAL_OPEN_BLOCKED_REASON } from "../components/AutopilotProvider";
import { useAutopilot } from "../components/autopilotContext";
import { useViewer } from "../auth/authContext";
import { AutopilotTakeControlPopover } from "../components/AutopilotTakeControlPopover";
import { PositionsSummaryRow } from "../components/PositionsSummaryRow";
import { PositionActionModal } from "../components/PositionActionModal";
import { OperationStatus } from "../components/OperationStatus";
import { PositionCard } from "../components/PositionCard";
import { SpotPositionCard } from "../components/spot/SpotPositionCard";
import { PriceScale } from "../components/PriceScale";
import { StopDistanceControl } from "../components/StopDistanceControl";
import { buildStopRowView, formatStopDistance, mapStopPrefill } from "../lib/stopPolicy";
import { priceFrame } from "../lib/priceFrame";
import type { BalanceRead, Operation, Position } from "../types";
import { RuntimeStatusContext } from "../api/runtimeStatusContext";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { formatAgo, formatPercent, formatPrice, shorten } from "../lib/format";
import { RefreshButton } from "../components/RefreshButton";
import { useRefreshPhase } from "../lib/useRefreshPhase";

type LoadedPositions = Awaited<ReturnType<PositionActions["loadPositionPermissions"]>> & {
  attemptedTransitions: Operation[];
};

function operationTime(operation: Operation, field: "updatedAt" | "createdAt") {
  const value = Date.parse(operation[field]);
  return Number.isFinite(value) ? value : Number.NEGATIVE_INFINITY;
}

interface OperationObservation {
  operation: Operation;
  epoch: number;
}

const OPERATION_STATE_PROGRESS: Record<string, number> = {
  queued: 0,
  preparing: 1,
  prepared: 2,
  submitted: 3,
  unknown: 3,
  confirmed: 4,
  provisional: 4,
  reconciling: 5,
  blocked: 6,
  failed: 6,
  "needs-attention": 7,
  cancelled: 7,
  completed: 7,
};

const STEP_STATE_PROGRESS: Record<string, number> = {
  pending: 0,
  prepared: 1,
  unknown: 2,
  provisional: 3,
  "expired-absent": 4,
  "finalized-error": 4,
  "finalized-success": 4,
  cancelled: 4,
};

const COMPLETED_OPEN_REFRESH_DELAY_MS = 5_000;

function compareOpeningProgress(left: Operation, right: Operation) {
  const fields = ["setupState", "fundingSwapState", "depositState"] as const;
  for (const field of fields) {
    const progress =
      (STEP_STATE_PROGRESS[left.opening?.[field] ?? ""] ?? -1) -
      (STEP_STATE_PROGRESS[right.opening?.[field] ?? ""] ?? -1);
    if (progress !== 0) return progress;
  }
  return 0;
}

function compareOperationProgress(left: Operation, right: Operation) {
  if (left.id !== right.id) {
    return (
      operationTime(left, "createdAt") - operationTime(right, "createdAt") ||
      operationTime(left, "updatedAt") - operationTime(right, "updatedAt")
    );
  }
  const leftRevision = left.intentRevision?.revision ?? 0;
  const rightRevision = right.intentRevision?.revision ?? 0;
  return (
    leftRevision - rightRevision ||
    operationTime(left, "updatedAt") - operationTime(right, "updatedAt") ||
    left.attempts - right.attempts ||
    (OPERATION_STATE_PROGRESS[left.state] ?? -1) - (OPERATION_STATE_PROGRESS[right.state] ?? -1) ||
    compareOpeningProgress(left, right)
  );
}

function sameOperationBinding(left: Operation, right: Operation) {
  return (
    left.id === right.id &&
    (left.intentRevision?.revision ?? 0) === (right.intentRevision?.revision ?? 0) &&
    (left.intentRevision?.activeIntentHash ?? null) ===
      (right.intentRevision?.activeIntentHash ?? null)
  );
}

function newerObservation(current: OperationObservation, candidate: OperationObservation) {
  const progress = compareOperationProgress(candidate.operation, current.operation);
  if (progress !== 0) return progress > 0 ? candidate : current;
  return sameOperationBinding(candidate.operation, current.operation) &&
    candidate.epoch > current.epoch
    ? candidate
    : current;
}

function latestOperations(observations: OperationObservation[]) {
  const latest = new Map<string, OperationObservation>();
  for (const observation of observations) {
    const current = latest.get(observation.operation.id);
    latest.set(
      observation.operation.id,
      current ? newerObservation(current, observation) : observation,
    );
  }
  return [...latest.values()].map(({ operation }) => operation);
}

function latestPositionOperation(position: Position, observations: OperationObservation[]) {
  let latest = position.operation ? { operation: position.operation, epoch: 0 } : undefined;
  for (const observation of observations) {
    const { operation } = observation;
    if (operation.positionId !== position.id && operation.id !== position.operation?.id) continue;
    latest = latest ? newerObservation(latest, observation) : observation;
  }
  return latest?.operation;
}

function tokenSymbolOf(position: Position, mint: string) {
  return (
    [position.tokenA, position.tokenB].find((token) => token.mint === mint)?.symbol ?? shorten(mint)
  );
}

/**
 * The dialog's fixed "In force now"/"Saved" line in display sides: both always named, "no stop"
 * when absent.
 */
function inForceLine(rows: { below: string | null; above: string | null }, target: string) {
  const side = (value: string | null, word: string) =>
    value === null ? "no stop" : `${formatStopDistance(value)} ${word}`;
  return `${side(rows.below, "below")} · ${side(rows.above, "above")} · return to ${target}`;
}

function sideChanged(draft: string, saved: string | null) {
  return saved === null || Number(draft) !== Number(saved);
}

function PolicyModal({
  position,
  onClose,
  onSaved,
}: {
  position: Position;
  onClose: () => void;
  onSaved: (policy: ExitPolicy) => void;
}) {
  const current = position.exitPolicy;
  // Rows, prices and words are in the display frame; the request, `calculateStops` and
  // `stopTriggered` stay canonical. `rowsToCanonical` is the only display-to-stored mapping.
  const frame = priceFrame(position.tokenA, position.tokenB);
  const opening = position.monitorState === "opening";
  const hasTriggers = current.lowerTrigger !== undefined || current.upperTrigger !== undefined;
  const saved = opening ? hasTriggers : current.enabled;
  let savedDistances: { lower: string | null; upper: string | null } | null = null;
  if (hasTriggers) {
    try {
      savedDistances = stopDistances(current);
    } catch {
      savedDistances = null;
    }
  }
  const mapped = savedDistances ? mapStopPrefill(savedDistances, frame.inverted) : null;
  // Change detection compares against the UNFILLED saved rows: a missing legacy side stays null,
  // so the side the notice promises to add always counts as a change.
  const savedRows = savedDistances ? frame.distancesToRows(savedDistances) : null;
  const initialNotice =
    mapped?.notice ??
    (saved
      ? null
      : hasTriggers
        ? "No stop is in force; saving arms both."
        : "No stop is saved; saving adds both.");
  const [below, setBelow] = useState(mapped?.below ?? "5");
  const [above, setAbove] = useState(mapped?.above ?? "5");
  const [mirror, setMirror] = useState(mapped?.mirror ?? true);
  const [stopInvalid, setStopInvalid] = useState(false);
  const [targetMint, setTargetMint] = useState(
    [position.tokenA.mint, position.tokenB.mint].includes(current.targetMint)
      ? current.targetMint
      : position.tokenA.mint,
  );
  const inFlight = useRef(false);
  const closeButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const previous = document.activeElement;
    closeButton.current?.focus();
    return () => {
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, []);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const exitPolicy: ExitPolicyRequest = {
    enabled: true,
    side: "both",
    ...frame.rowsToCanonical({ below, above }),
    ...(current.thresholdInverted === undefined
      ? {}
      : { thresholdInverted: current.thresholdInverted }),
    targetMint,
  };
  let stops: { lowerTrigger?: string; upperTrigger?: string } = {};
  let geometryError: string | undefined;
  try {
    stops = calculateStops(position.lowerPrice, position.upperPrice, exitPolicy);
  } catch {
    geometryError =
      "Invalid stop geometry: inward stops must stay inside the LP range and the lower stop must stay below the upper stop.";
  }
  const shownStops = frame.stops(stops);
  const savedStops = frame.stops(current);
  const shownCurrent = frame.priceOrNull(position.currentPrice);
  let shownBounds: { lower: string; upper: string } | null = null;
  try {
    shownBounds = frame.bounds(position.lowerPrice, position.upperPrice);
  } catch {
    shownBounds = null;
  }
  const basisInverted = current.thresholdInverted === true;
  const basisUnit =
    basisInverted === frame.inverted
      ? null
      : priceFrame(position.tokenA, position.tokenB, basisInverted).unit;
  const targetSymbol = tokenSymbolOf(position, targetMint);
  const inForceText = !saved
    ? `no automatic exit · return to ${tokenSymbolOf(position, current.targetMint)}`
    : savedRows
      ? inForceLine(savedRows, tokenSymbolOf(position, current.targetMint)) +
        (opening ? " · inactive while opening" : "")
      : "—";
  const targetChanged = targetMint !== current.targetMint;
  const belowChanged = sideChanged(below, savedRows?.below ?? null);
  const aboveChanged = sideChanged(above, savedRows?.above ?? null);
  const canSave =
    !stopInvalid && !geometryError && (!saved || targetChanged || belowChanged || aboveChanged);
  const changeSentences: string[] = [];
  if (belowChanged)
    changeSentences.push(
      savedRows?.below !== null && savedRows?.below !== undefined
        ? `Below-range stop moves from ${
            savedStops.lowerStop ? formatPrice(savedStops.lowerStop) : "—"
          } to ${shownStops.lowerStop ? formatPrice(shownStops.lowerStop) : "—"}${
            Number(below) === 0 ? ", onto the range start" : ""
          }.`
        : `Below-range stop is added at ${
            shownStops.lowerStop ? formatPrice(shownStops.lowerStop) : "—"
          }.`,
    );
  if (aboveChanged)
    changeSentences.push(
      savedRows?.above !== null && savedRows?.above !== undefined
        ? `Above-range stop moves from ${
            savedStops.upperStop ? formatPrice(savedStops.upperStop) : "—"
          } to ${shownStops.upperStop ? formatPrice(shownStops.upperStop) : "—"}${
            Number(above) === 0 ? ", onto the range end" : ""
          }.`
        : `Above-range stop is added at ${
            shownStops.upperStop ? formatPrice(shownStops.upperStop) : "—"
          }.`,
    );
  if (targetChanged) changeSentences.push(`Proceeds will return as ${targetSymbol}.`);
  if (!saved && changeSentences.length === 0)
    changeSentences.push("The stops shown above are saved as they are.");
  // The dialog only holds a price snapshot, so it never promises that saving is harmless; it warns
  // when even that snapshot is already at or past a moved stop (the monitor would close at once).
  let alreadyPast = false;
  try {
    alreadyPast = position.currentPrice !== null && stopTriggered(position.currentPrice, stops);
  } catch {
    alreadyPast = false;
  }
  const closingSentence = alreadyPast
    ? opening
      ? "The price is already past a stop, so the position can close as soon as the opening completes."
      : "The price is already past a stop, so saving can close the position right away."
    : opening
      ? "It arms when the opening completes and closes the position once the price reaches a stop."
      : "It arms the moment you save and closes the position once the price reaches a stop.";
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (inFlight.current || !canSave) return;
    inFlight.current = true;
    setPending(true);
    setError(null);
    try {
      const savedPolicy = await api<ExitPolicy>(
        `/positions/${encodeURIComponent(position.id)}/policy`,
        {
          method: "POST",
          body: {
            exitPolicy,
          } satisfies UpdateExitPolicyRequest,
          sensitive: true,
        },
      );
      onSaved(savedPolicy);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not save stop policy");
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  }
  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (!pending && event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="modal action-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="policy-title"
        onKeyDown={(event) => {
          if (!pending && event.key === "Escape") {
            event.stopPropagation();
            onClose();
          }
        }}
      >
        <header className="modal-header">
          <h2 id="policy-title">Change stop</h2>
          <button
            ref={closeButton}
            className="icon-button"
            onClick={onClose}
            aria-label="Close"
            disabled={pending}
          >
            ×
          </button>
        </header>
        <p className="modal-subtitle">
          {position.pair} · {formatPercent(position.feePercent)} · position{" "}
          {shorten(position.id, 7)}
        </p>
        <form className="stack" onSubmit={(event) => void submit(event)}>
          <p className="scale-note">
            <span>{opening ? "Saved" : "In force now"}</span> · {inForceText}
          </p>
          {basisUnit !== null && (
            <p className="scale-note">
              Distances are measured in {basisUnit}. Direction and preview use {frame.unit}.
            </p>
          )}
          <StopDistanceControl
            below={below}
            above={above}
            onChange={(next) => {
              setBelow(next.below);
              setAbove(next.above);
            }}
            mirror={mirror}
            onMirror={setMirror}
            onValidityChange={setStopInvalid}
            geometryError={geometryError}
            rows={{
              below: buildStopRowView(shownStops.lowerStop, shownCurrent ?? "", null),
              above: buildStopRowView(shownStops.upperStop, shownCurrent ?? "", null),
            }}
            unit={frame.unit}
            usdReason="USD value unavailable"
            notice={initialNotice}
            tokens={[position.tokenA, position.tokenB]}
            targetMint={targetMint}
            onTarget={setTargetMint}
          />
          {shownBounds && (
            <PriceScale
              min={shownBounds.lower}
              max={shownBounds.upper}
              current={shownCurrent}
              lowerStop={shownStops.lowerStop}
              upperStop={shownStops.upperStop}
              preliminary
              unit={frame.unit}
            />
          )}
          {changeSentences.length > 0 && (
            <p className="hint">
              {changeSentences.join(" ")} {closingSentence}
            </p>
          )}
          {error && (
            <p className="inline-alert error" role="alert">
              {error}
            </p>
          )}
          <button className="button danger wide" disabled={pending || !canSave}>
            {pending ? "Saving…" : "Save stop"}
          </button>
        </form>
      </section>
    </div>
  );
}

export function PositionsPage({
  refresh,
  onBrowsePools,
  onOpenSettings,
  onOpenAutopilot,
  actions: sharedActions,
}: {
  refresh: number;
  onBrowsePools?: () => void;
  onOpenSettings?: () => void;
  onOpenAutopilot?: () => void;
  actions?: PositionActions;
}) {
  const autopilot = useAutopilot();
  // A viewer never reaches a close, resume, policy or take-over dialog (the card hides their
  // triggers as well); the server refuses every one of those routes to a viewer session anyway.
  const viewer = useViewer();
  const localActions = usePositionActions(!sharedActions, !sharedActions, refresh);
  const actions = sharedActions ?? localActions;
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const runtime = useContext(RuntimeStatusContext);
  const runtimeMonitor =
    runtime?.freshness === "fresh" && !runtime.error ? runtime.data?.rpc : null;
  const observedTransitions = useRef(new Map<string, string>());
  const pendingTransitions = useRef(new Map<string, Operation>());
  // The intent of the next load, read when it starts: the first load of a mount and a newer
  // shared snapshot ask for the snapshot, every other reload reads live, and live always wins.
  const snapshotWanted = useRef(true);
  const liveWanted = useRef(false);
  // The newest response loaded, recorded by the loader itself: the next coalesced run starts
  // before React renders the previous result, so `positions.data` can still be older.
  const newestPositions = useRef<LoadedPositions | null>(null);
  const positions = useResource(
    "positions",
    async (signal): Promise<LoadedPositions> => {
      const snapshot = snapshotWanted.current && !liveWanted.current;
      snapshotWanted.current = false;
      liveWanted.current = false;
      const attemptedTransitions = [...pendingTransitions.current.values()];
      const loaded = {
        ...(await actions.loadPositionPermissions(signal, { snapshot })),
        attemptedTransitions,
      };
      // A late snapshot never replaces a newer or equal read.
      const newest = newestPositions.current;
      if (snapshot && newest && !(Date.parse(loaded.updatedAt) > Date.parse(newest.updatedAt)))
        return newest;
      newestPositions.current = loaded;
      return loaded;
    },
    undefined,
    true,
  );
  // Data set by other paths (a saved policy) is followed, never data older than the newest load.
  if (
    positions.data &&
    !(
      newestPositions.current &&
      Date.parse(positions.data.updatedAt) < Date.parse(newestPositions.current.updatedAt)
    )
  )
    newestPositions.current = positions.data;
  const reloadSnapshot = positions.reload;
  const reloadLive = useCallback(() => {
    liveWanted.current = true;
    reloadSnapshot();
  }, [reloadSnapshot]);
  useEffect(() => {
    const onSnapshot = (event: Event) => {
      const { updatedAt } = (event as CustomEvent<{ updatedAt: string }>).detail;
      const newest = newestPositions.current;
      if (newest && !(Date.parse(updatedAt) > Date.parse(newest.updatedAt))) return;
      snapshotWanted.current = true;
      reloadSnapshot();
    };
    window.addEventListener("auto-pools:positions-snapshot", onSnapshot);
    return () => {
      window.removeEventListener("auto-pools:positions-snapshot", onSnapshot);
    };
  }, [reloadSnapshot]);
  const operations = actions.operations;
  const observePositions = actions.observePositions;
  const operationObservations = useMemo<OperationObservation[]>(
    () => [
      ...(positions.data?.items.flatMap(({ operation }) =>
        operation ? [{ operation, epoch: 0 }] : [],
      ) ?? []),
      ...(positions.data?.operations.map((operation) => ({
        operation,
        epoch: positions.data?.epoch ?? 0,
      })) ?? []),
      ...(operations.data?.items.map((operation) => ({
        operation,
        epoch: operations.data?.epoch ?? 0,
      })) ?? []),
    ],
    [operations.data, positions.data],
  );
  useEffect(() => {
    if (positions.data) observePositions(positions.data.items);
  }, [positions.data, observePositions]);
  const reloadPositions = reloadLive;
  useEffect(() => {
    for (const op of latestOperations(operationObservations)) {
      if (
        op.kind !== "open" ||
        !["blocked", "failed"].includes(op.state) ||
        op.opening?.executionVersion !== 2 ||
        op.opening.setupState !== "finalized-success"
      )
        continue;
      const key = JSON.stringify([
        op.id,
        op.state,
        op.step,
        op.attempts,
        op.intentRevision?.revision,
        op.intentRevision?.activeIntentHash,
        op.opening.setupState,
        op.opening.fundingSwapState,
        op.opening.depositState,
      ]);
      if (observedTransitions.current.get(op.id) === key) continue;
      observedTransitions.current.set(op.id, key);
      pendingTransitions.current.set(op.id, op);
    }
    // Polls observed during a load still invalidate rendered authority. Only the positions
    // response covers a permission attempt; its later causal operations read may be newer.
    if (!positions.loading && !positions.error && positions.data) {
      for (const [id, pending] of pendingTransitions.current) {
        const covered = positions.data.items.find((p) => p.operation?.id === id)?.operation;
        const attempted = positions.data.attemptedTransitions.find((op) => op.id === id);
        if (
          [covered, attempted].some(
            (op) =>
              op && sameOperationBinding(op, pending) && compareOperationProgress(op, pending) >= 0,
          )
        )
          pendingTransitions.current.delete(id);
      }
      if (pendingTransitions.current.size) reloadPositions();
    }
    while (observedTransitions.current.size > 256) {
      const id = observedTransitions.current.keys().next().value ?? "";
      observedTransitions.current.delete(id);
      pendingTransitions.current.delete(id);
    }
  }, [operationObservations, positions.data, positions.loading, positions.error, reloadPositions]);
  const completionSeen = useRef(actions.completionVersion);
  useEffect(() => {
    if (completionSeen.current === actions.completionVersion) return;
    completionSeen.current = actions.completionVersion;
    reloadPositions();
  }, [actions.completionVersion, reloadPositions]);
  const completedOpeningRefreshes = useMemo(() => {
    const candidates: { operationId: string; positionId: string }[] = [];
    for (const position of positions.data?.items ?? []) {
      if (position.monitorState !== "opening") continue;
      const action = actions.actions[position.id];
      const operation = latestPositionOperation(position, [
        ...operationObservations,
        ...(action?.operation
          ? [{ operation: action.operation, epoch: action.acceptedEpoch ?? 0 }]
          : []),
      ]);
      if (operation?.kind === "open" && operation.state === "completed")
        candidates.push({ operationId: operation.id, positionId: position.id });
    }
    return candidates;
  }, [actions.actions, operationObservations, positions.data?.items]);
  const pendingCompletedOpeningRefreshes = useRef(
    new Map<string, { positionId: string; expiresAt: number }>(),
  );
  const consumedCompletedOpeningRefreshes = useRef(new Set<string>());
  const completedOpeningRefreshTimer = useRef<number | null>(null);
  useEffect(() => {
    const pending = pendingCompletedOpeningRefreshes.current;
    const consumed = consumedCompletedOpeningRefreshes.current;
    const current = new Map(
      completedOpeningRefreshes.map(({ operationId, positionId }) => [operationId, positionId]),
    );
    for (const [operationId, refresh] of pending) {
      if (current.get(operationId) !== refresh.positionId) pending.delete(operationId);
    }
    for (const [operationId, positionId] of current) {
      if (!pending.has(operationId) && !consumed.has(operationId))
        pending.set(operationId, {
          positionId,
          expiresAt: Date.now() + COMPLETED_OPEN_REFRESH_DELAY_MS,
        });
    }
    let excessConsumed = consumed.size - 256;
    for (const operationId of consumed) {
      if (excessConsumed <= 0) break;
      if (current.has(operationId)) continue;
      consumed.delete(operationId);
      excessConsumed--;
    }

    const schedule = () => {
      const nextExpiry = Math.min(...[...pending.values()].map(({ expiresAt }) => expiresAt));
      if (!Number.isFinite(nextExpiry)) return;
      completedOpeningRefreshTimer.current = window.setTimeout(
        () => {
          completedOpeningRefreshTimer.current = null;
          const now = Date.now();
          let refresh = false;
          for (const [operationId, entry] of pending) {
            if (entry.expiresAt > now) continue;
            pending.delete(operationId);
            consumed.add(operationId);
            refresh = true;
          }
          if (refresh) reloadPositions();
          schedule();
        },
        Math.max(0, nextExpiry - Date.now()),
      );
    };
    if (completedOpeningRefreshTimer.current !== null)
      window.clearTimeout(completedOpeningRefreshTimer.current);
    schedule();
    return () => {
      if (completedOpeningRefreshTimer.current !== null) {
        window.clearTimeout(completedOpeningRefreshTimer.current);
        completedOpeningRefreshTimer.current = null;
      }
    };
  }, [completedOpeningRefreshes, reloadPositions]);
  // The server captures the balance inside the positions pass, so it is read once per settled
  // positions load: no mount request of its own and no polling. The Bybit subaccount's part of
  // Total and the split (In spot, Free cash) come from the same server read, so a change in the
  // spot leg reads it again too — free USDT included, which moves on its own when a deposit is
  // reconciled while an unpriced coin keeps the value unread.
  const [balance, setBalance] = useState<{
    data: BalanceRead | null;
    error: Error | null;
    loading: boolean;
  }>({ data: null, error: null, loading: false });
  const balanceRequest = useRef<AbortController | null>(null);
  const loadBalance = useCallback(() => {
    balanceRequest.current?.abort();
    const controller = new AbortController();
    balanceRequest.current = controller;
    setBalance((current) => ({ ...current, loading: true }));
    void api<BalanceRead>("/balance", { signal: controller.signal }).then(
      (data) => {
        if (!controller.signal.aborted) setBalance({ data, error: null, loading: false });
      },
      (reason: unknown) => {
        if (controller.signal.aborted) return;
        setBalance((current) => ({
          data: current.data,
          error: reason instanceof Error ? reason : new Error("Balance unavailable"),
          loading: false,
        }));
      },
    );
  }, []);
  const positionsLoading = useRef(positions.loading);
  const balanceRead = useRef(false);
  useEffect(() => {
    const settled = positionsLoading.current && !positions.loading;
    positionsLoading.current = positions.loading;
    if (!settled) return;
    balanceRead.current = true;
    loadBalance();
  }, [positions.loading, loadBalance]);
  // A server restart (every deploy) forgets the balance until a positions pass captures it again,
  // and this page reads positions only on entry or on demand. So a tab left open across it would
  // keep showing "Balance unavailable": read positions once per server start (`at` is the process
  // start time), which also caps a failing positions read at one retry. A load in flight captures
  // anyway and re-reads the balance when it settles, so it is left to answer first. Without a
  // wallet nothing is ever captured, so only a summary that is itself not read yet counts.
  const notReadHandledAt = useRef<string | null>(null);
  useEffect(() => {
    const refresh = balance.data?.lastRefresh;
    if (!refresh || refresh.ok || refresh.reason !== BALANCE_NOT_READ) return;
    if (balance.data?.balance?.reason !== BALANCE_NOT_READ) return;
    if (positionsLoading.current || notReadHandledAt.current === refresh.at) return;
    notReadHandledAt.current = refresh.at;
    reloadPositions();
  }, [balance.data, reloadPositions]);
  const spot = autopilot.data?.spot;
  const spotBalanceKey = spot
    ? `${spot.lifecycle}|${spot.card.value ?? "unread"}|${spot.card.freeUsdt ?? "unread"}`
    : "none";
  const spotBalanceSeen = useRef(spotBalanceKey);
  useEffect(() => {
    if (spotBalanceSeen.current === spotBalanceKey) return;
    spotBalanceSeen.current = spotBalanceKey;
    // Before the first settled positions load the coupled read above will include it anyway.
    if (balanceRead.current) loadBalance();
  }, [spotBalanceKey, loadBalance]);
  useEffect(
    () => () => {
      balanceRequest.current?.abort();
    },
    [],
  );
  const [ringedPosition, setRingedPosition] = useState<string | null>(null);
  const ringTimer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (ringTimer.current !== null) window.clearTimeout(ringTimer.current);
    },
    [],
  );
  function showPosition(id: string) {
    const card = document.getElementById(`position-${id}`);
    card?.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      block: "center",
    });
    document.getElementById(`position-heading-${id}`)?.focus({ preventScroll: true });
    setRingedPosition(id);
    if (ringTimer.current !== null) window.clearTimeout(ringTimer.current);
    ringTimer.current = window.setTimeout(() => {
      ringTimer.current = null;
      setRingedPosition(null);
    }, 1_200);
  }
  const visiblePositions = [...(positions.data?.items ?? [])];
  for (const action of Object.values(actions.actions)) {
    const position = action?.position;
    if (
      position &&
      action.operation &&
      action.operation.state !== "completed" &&
      !visiblePositions.some((p) => p.id === position.id)
    )
      visiblePositions.push(position);
  }
  const cards = visiblePositions
    .filter((p) => actions.actions[p.id]?.operation?.state !== "completed")
    .map((position) => {
      const action = actions.actions[position.id];
      let operation = latestPositionOperation(position, [
        ...operationObservations,
        ...(action?.operation
          ? [{ operation: action.operation, epoch: action.acceptedEpoch ?? 0 }]
          : []),
      ]);
      if (operation?.kind === "open" && operation.retry && (positions.loading || positions.error)) {
        const unavailable = {
          allowed: false,
          reasonCode: "EVIDENCE_UNAVAILABLE" as const,
          reason: positions.loading
            ? "Checking opening permissions…"
            : "Permission check unavailable. Check again.",
        };
        operation = {
          ...operation,
          retry: {
            ...operation.retry,
            cancel: unavailable,
            sameLimits: unavailable,
            retryWithSlippageAllowed: false,
            // A reopen starts with the same cleanup, so it is withheld with the same proof.
            ...(operation.retry.autoReopen
              ? { autoReopen: { ...unavailable, maxSlippageBps: null } }
              : {}),
          },
        };
      }
      return { position, action, operation };
    });
  // A finished sequence cleanup must never reach the legacy "Opening cancelled" card presentation.
  const sequenceCards = cards.filter(
    ({ operation }) =>
      operation?.autoReopen && operation.step === "cleanup" && operation.state === "completed",
  );
  const positionCards = cards.filter((card) => !sequenceCards.includes(card));
  const carded = new Set(
    positionCards.flatMap(({ operation }) => (operation ? [operation.id] : [])),
  );
  const sequencePair = (positionId: string | undefined) =>
    positionId
      ? (positions.data?.items.find((p) => p.id === positionId)?.pair ??
        actions.actions[positionId]?.position?.pair ??
        actions.actions[positionId]?.pair ??
        null)
      : null;
  // Exactly one presentation per sequence: the replaced card, otherwise an orphan panel. The
  // replaced position only supplies the title; the newest observed member stays the shown operation.
  const sequencePanels: { id: string; pair: string | null; operation: Operation }[] = [];
  for (const { position, operation } of sequenceCards) {
    const id = operation?.autoReopen?.sequenceId;
    if (!operation || !id || sequencePanels.some((panel) => panel.id === id)) continue;
    const observed = actions.sequences[id]?.operation;
    sequencePanels.push({
      id,
      pair: position.pair,
      operation: observed
        ? newerObservation({ operation, epoch: 0 }, { operation: observed, epoch: 1 }).operation
        : operation,
    });
  }
  for (const latest of Object.values(actions.sequences)) {
    if (!latest || sequencePanels.some((panel) => panel.id === latest.sequence.sequenceId))
      continue;
    const { sequence } = latest;
    const members = [
      sequence.sourceOperationId,
      sequence.cleanupOperationId,
      sequence.successorOperationId,
      sequence.manualSuccessorOperationId,
    ];
    if (members.some((member) => member && carded.has(member))) continue;
    sequencePanels.push({
      id: sequence.sequenceId,
      pair: sequencePair(latest.operation.positionId),
      operation: latest.operation,
    });
  }
  const [policyPosition, setPolicyPosition] = useState<Position | null>(null);
  const [resumeOperation, setResumeOperation] = useState<Operation | null>(null);
  const [takeControlPosition, setTakeControlPosition] = useState<Position | null>(null);
  const refreshButton = useRefreshPhase({
    pending: positions.loading,
    failed: positions.error !== null,
  });
  const count = positions.data
    ? visiblePositions.filter((p) => actions.actions[p.id]?.operation?.state !== "completed").length
    : undefined;
  // The heading counts every card in the list (design 58): the spot card is one of them.
  const spotLegs = autopilot.data?.spot ? 1 : 0;
  const listed = count === undefined ? undefined : count + spotLegs;
  const parts = [
    ...(count ? [`${String(count)} ${count === 1 ? "pool" : "pools"}`] : []),
    ...(spotLegs ? ["1 spot leg"] : []),
  ];
  const updated =
    refreshButton.phase === "done" ? (
      <span className="refresh-confirmed">updated just now</span>
    ) : positions.data?.updatedAt ? (
      `updated ${formatAgo(positions.data.updatedAt)}`
    ) : null;
  return (
    <>
      <header className="positions-toolbar">
        <div className="positions-context">
          <strong>
            {listed === undefined
              ? "Positions"
              : listed === 0
                ? "No positions"
                : `${String(listed)} ${listed === 1 ? "position" : "positions"}`}
          </strong>
          {(parts.length > 0 || updated !== null) && (
            <span className="positions-subline">
              {parts.join(" · ")}
              {parts.length > 0 && updated !== null && " · "}
              {updated}
            </span>
          )}
        </div>
        <div className="positions-toolbar-actions">
          <RefreshButton
            className="positions-refresh"
            phase={refreshButton.phase}
            ariaLabel="Refresh"
            title="Refresh positions"
            onClick={() => {
              if (refreshButton.start()) reloadLive();
            }}
          />
          {count !== undefined && count > 0 && (
            <button
              className="button primary new-position"
              onClick={onBrowsePools}
              disabled={autopilot.blocksManualOpening}
              title={
                autopilot.blocksManualOpening ? AUTOPILOT_MANUAL_OPEN_BLOCKED_REASON : undefined
              }
            >
              <svg viewBox="0 0 14 14" aria-hidden="true">
                <path d="M7 2v10M2 7h10" />
              </svg>
              New position
            </button>
          )}
        </div>
      </header>
      {operations.error && (
        <p className="inline-alert error" role="alert">
          Operation updates unavailable. Last known status is shown.
        </p>
      )}
      {positions.error && (
        <p className="inline-alert error" role="alert">
          {positions.error.message}
          <button className="button" onClick={reloadLive} disabled={positions.loading}>
            Check again
          </button>
        </p>
      )}
      <PositionsSummaryRow
        view={autopilot.data ?? null}
        poolCount={count}
        balance={{
          data: balance.data,
          error: balance.error,
          // The first coupled read is not settled while positions are still loading: Total stays a
          // skeleton instead of claiming an unavailable balance.
          loading: balance.loading || positions.loading,
          positionsError: positions.error !== null,
          onCheckAgain: reloadLive,
          onOpenSettings,
          onShowPosition: showPosition,
        }}
        onOpenAutopilot={onOpenAutopilot}
      />
      <section className="positions-list" aria-busy={positions.loading}>
        {autopilot.data?.spot && (
          <SpotPositionCard
            spot={autopilot.data.spot}
            expanded={expanded.has("spot")}
            onToggle={() => {
              setExpanded((old) => {
                const next = new Set(old);
                if (next.has("spot")) next.delete("spot");
                else next.add("spot");
                return next;
              });
            }}
            onOpenJournal={onOpenAutopilot}
            onOpenSettings={onOpenSettings}
          />
        )}
        {positions.loading && !positions.data && (
          // A card list loads into a card-sized placeholder, as History does. `.center-state`
          // reserves half the viewport, which belongs to a whole-screen state such as sign-in.
          <div className="operation-skeleton" role="status">
            Reconciling positions…
          </div>
        )}
        {positionCards.map(({ position, action, operation }) => {
          const openingFunding =
            action?.position?.operation?.opening?.funding ?? position.operation?.opening?.funding;
          return (
            <PositionCard
              key={position.id}
              actionPending={action?.pending}
              openingFunding={openingFunding}
              lastKnown={!positions.data?.items.some((p) => p.id === position.id)}
              ringed={ringedPosition === position.id}
              expanded={expanded.has(position.id)}
              onToggle={() => {
                setExpanded((old) => {
                  const next = new Set(old);
                  if (next.has(position.id)) next.delete(position.id);
                  else next.add(position.id);
                  return next;
                });
              }}
              onOperation={() => {
                operations.reload();
              }}
              onRestart={() => {
                const prefill = operation?.opening?.prefill;
                if (prefill) actions.begin({ ...position, operation }, prefill);
              }}
              onResume={setResumeOperation}
              runtimeMonitor={runtimeMonitor}
              position={operation === position.operation ? position : { ...position, operation }}
              onClose={() => {
                actions.begin(position);
              }}
              onPolicy={() => {
                setPolicyPosition(position);
              }}
              onTakeControl={() => {
                setTakeControlPosition(position);
              }}
            />
          );
        })}
        {operations.data?.items
          .filter(
            (op) =>
              op.kind === "close" &&
              !op.autoReopen &&
              !["completed", "cancelled"].includes(op.state) &&
              !visiblePositions.some((p) => p.id === op.positionId),
          )
          .map((op) => (
            <section key={op.id}>
              <h2 id={`position-heading-${op.positionId ?? op.id}`} tabIndex={-1}>
                Position operation
              </h2>
              <p>Position values unavailable. The journal continues tracking this operation.</p>
              <OperationStatus operation={op} onResume={setResumeOperation} />
            </section>
          ))}
        {sequencePanels.map(({ id, pair, operation }) => (
          <section key={id}>
            <h2 id={`position-heading-${operation.positionId ?? operation.id}`} tabIndex={-1}>
              {pair ? `Reopening ${pair}` : "Reopening position"}
            </h2>
            <p>The server runs this sequence; the journal keeps tracking its operations.</p>
            <OperationStatus operation={operation} />
          </section>
        ))}
        {count === 0 && (
          <div className="positions-empty">
            <svg className="positions-empty-graphic" viewBox="0 0 120 44" aria-hidden="true">
              <rect x="0" y="17" width="120" height="10" rx="5" />
              <rect x="28" y="17" width="56" height="10" rx="5" />
              <rect x="16" y="9" width="2" height="26" rx="1" />
              <rect x="100" y="9" width="2" height="26" rx="1" />
              <circle cx="62" cy="22" r="6" />
            </svg>
            <div>
              <b>No positions yet</b>
              <span>
                Pick a pool, set a range and an auto-exit rule. Active positions appear here with
                live P&amp;L.
              </span>
            </div>
            <button className="button primary browse-pools" onClick={onBrowsePools}>
              Browse pools
              <svg viewBox="0 0 14 14" aria-hidden="true">
                <path d="M2.5 7h9M8 3.5 11.5 7 8 10.5" />
              </svg>
            </button>
          </div>
        )}
      </section>
      {!viewer && actions.dialog?.position && (
        <PositionActionModal
          title="Confirm position action"
          description=""
          endpoint={`/positions/${encodeURIComponent(actions.dialog.position.id)}/close`}
          position={actions.dialog.position}
          payload={actions.dialog.cleanup ? { cleanupOnly: true } : undefined}
          submitLabel="Confirm"
          onClose={actions.dismiss}
          action={{
            pending: actions.dialog.pending,
            error: actions.dialog.error,
            failure: actions.dialog.failure,
            submit: () => actions.submit(actions.dialog?.position?.id ?? ""),
          }}
        />
      )}
      {!viewer && resumeOperation && (
        <PositionActionModal
          title="Close again?"
          description="The server re-runs the approved close from the first step it has not finalized: withdrawn fees are not withdrawn again, and a completed withdrawal goes straight to returning the funds."
          endpoint={`/operations/${encodeURIComponent(resumeOperation.id)}/resume`}
          submitLabel="Close again"
          onAccepted={() => {
            setResumeOperation(null);
            operations.reload();
            reloadLive();
          }}
          onClose={() => {
            setResumeOperation(null);
          }}
        />
      )}
      {!viewer && policyPosition && !policyPosition.readOnly && (
        <PolicyModal
          position={policyPosition}
          onClose={() => {
            setPolicyPosition(null);
          }}
          onSaved={(savedPolicy) => {
            positions.updateData((current) =>
              current
                ? {
                    ...current,
                    items: current.items.map((position) =>
                      position.id === policyPosition.id
                        ? {
                            ...position,
                            exitPolicy: savedPolicy,
                            rangeMarks: position.openingSetup
                              ? rangeOrientations({
                                  opening: position.openingSetup,
                                  executedLower: position.lowerPrice,
                                  executedUpper: position.upperPrice,
                                  lowerTrigger: savedPolicy.lowerTrigger ?? null,
                                  upperTrigger: savedPolicy.upperTrigger ?? null,
                                  markerPrice: position.currentPrice,
                                  tokenAMint: position.tokenA.mint,
                                  tokenBMint: position.tokenB.mint,
                                })
                              : null,
                          }
                        : position,
                    ),
                  }
                : current,
            );
            setPolicyPosition(null);
            reloadLive();
          }}
        />
      )}
      {!viewer && takeControlPosition && (
        <AutopilotTakeControlPopover
          position={takeControlPosition}
          onClose={() => {
            setTakeControlPosition(null);
          }}
          onReleased={() => {
            setTakeControlPosition(null);
            autopilot.reload();
            reloadLive();
          }}
        />
      )}
    </>
  );
}
